import React, { useCallback, useEffect } from 'react';
import {
  CustomCheckbox,
  CustomSelect,
  CustomSelectItem,
  Header,
  Button,
  useSetLayoutProps,
  FeatureFlagInfoWrapper,
  useFeatureFlag,
} from '@hwm/ui-lib';
import { Trans, t } from '@lingui/macro';
import {
  Slider,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Switch,
} from '@material-ui/core';
import { MenuOpen } from '@material-ui/icons';
import { isNil } from 'lodash';
import { DateRangePicker } from '@shared/components/DateRangePicker/DateRangePicker';
import { IAppLayoutProps } from '../Layout/Layout';
import { MapFilters, setMapTogglers, setDisplayCorrelateSites } from '@core/store/slices/map/map.reducer';
import { AppFeatureFlags } from '@root/appPermissions';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { selectMapTogglersState } from '@core/store/slices/map/map.selectors';
import { EMetricSystem } from '@models/EMetricSystem';
import { SurveyResultOptionsFn, SurveyStatusOptionsFn } from '@models/Surveys';

export const MAP_TOGGLERS_WIDTH = 280;

const useTogglerStyles = makeStyles({
  outerWrapper: {
    top: 0,
    left: 0,
    width: MAP_TOGGLERS_WIDTH,
    maxWidth: MAP_TOGGLERS_WIDTH,
    transition: 'left .4s cubic-bezier(0.820, 0.085, 0.395, 0.895)',
    zIndex: 1000,
  },

  innerWrapper: {
    '& > *:not(:last-child)': {
      marginBottom: '8px',
    },
  },

  togglersContainer: {
    borderRadius: '9px',
    padding: '8px 15px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    fontWeight: 600,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  select: {
    height: 36,
    maxHeight: 36,
    width: '100%',
    zIndex: 100,
    '& .select__control': {
      minWidth: 100,
    },
  },
  closed: {
    left: `-${MAP_TOGGLERS_WIDTH + 3}px`,
    opacity: 0,
  },
  togglerOpened: {
    boxShadow: 'none',
    paddingRight: 0,
    '& .MuiButton-label': {
      justifyContent: 'end',
    },
    '&:hover': {
      boxShadow: 'none',
    },
  },
  togglerClosed: {
    backgroundColor: 'white',
    textAlign: 'center',
    height: '42px',
    zIndex: 10,
  },
  closeIcon: {
    fontSize: '30px',
    width: '30px',
    height: '30px',
    cursor: 'pointer',
    marginRight: '10px',
    zIndex: 10,
    transition: 'transform 0.3s',
    willChange: 'transform',
    transform: ({ siderExpanded }: { siderExpanded: boolean }) => (siderExpanded ? 'rotate(0)' : 'rotate(180deg)'),
  },
});

export enum SitesShowFilter {
  ShowAllSites = 'ShowAllSites',
  OnlyShowActiveSites = 'OnlyShowActiveSites',
  OnlyShowInactiveSites = 'OnlyShowInactiveSites',
}

export const showOptionsFn = () => [
  {
    label: t`All sites`,
    value: SitesShowFilter.ShowAllSites,
  },
  {
    label: t`Only show active sites`,
    value: SitesShowFilter.OnlyShowActiveSites,
  },
  {
    label: t`Only show inactive sites`,
    value: SitesShowFilter.OnlyShowInactiveSites,
  },
];

interface TogglersProps {
  data: Partial<MapFilters>;
  additionalData?: { displayCorrelateSites?: boolean };
  setData: (payload: Partial<MapFilters>) => void;
  isDma?: boolean;
  isCorrelationInfo?: boolean;
}

const Togglers: React.FC<TogglersProps> = ({ data, additionalData = {}, setData, isDma, isCorrelationInfo }) => {
  const {
    showCSQ,
    showLNS,
    showName,
    showOption,
    showCorrelations,
    showGIS,
    showGISPipeData,
    showMeasurementTool,
    showGISPipeInformation,
    gisPipeMetricSystem,
    showGISAssetsData,
    showGISAssetIds,
    showGISDMAData,
    dateRange,
    showHighConfidenceOnly,
    showGISCorrelations,
    showSurveyMeasurements,
    showLinkedSites,
    showPRVLogger,
    showFlowLogger,
    showPressureLogger,
    showFlowPressureLogger,
    showSitesWithoutLocation,
    showThresholdModelling,
    thresholdModelling,
    showSurveys,
    surveysDateRange,
    surveysStatus,
    surveysResult,
  } = data;
  const { displayCorrelateSites } = additionalData;

  const dispatch = useAppDispatch();

  const open = useAppSelector(selectMapTogglersState);
  const setOpen = useCallback((newState: boolean) => dispatch(setMapTogglers(newState)), [dispatch]);

  const theme = useTheme();
  const classes = useTogglerStyles({ siderExpanded: open });
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [, props] = useSetLayoutProps<IAppLayoutProps>();

  const { hasFeatureFlag } = useFeatureFlag();

  const togglerButton = (styles: string, insideSidebar?: boolean) => (
    <Button
      key={'toggler_btn'}
      color="default"
      onClick={() => setOpen(!open)}
      className={styles}
      style={{ backgroundColor: 'white', color: 'black' }}
      data-testid={`mapFilters${insideSidebar ? '-sidebar' : ''}-toggle-button`}
    >
      <MenuOpen className={`${classes.closeIcon}`} />
    </Button>
  );

  const canUseThresholdModelling = !isNil(showThresholdModelling);

  useEffect(() => {
    if (isNil(isCorrelationInfo)) {
      if (!smallScreen || !props?.siderExpanded || !open) return;
      setOpen(false);
    }
  }, [open, smallScreen, props?.siderExpanded, isCorrelationInfo, setOpen]);

  return (
    <>
      <div className="absolute top-2 left-2 space-y-1">
        {togglerButton(classes.togglerClosed)}
        {!isCorrelationInfo && isDma && (
          <FormControlLabel
            className="ml-auto w-max flex flex-row-reverse pl-2 bg-white rounded-lg"
            data-testid="map-filters-correlateMode-switch"
            control={
              <Switch
                checked={displayCorrelateSites}
                value={displayCorrelateSites}
                onChange={(e) => dispatch(setDisplayCorrelateSites(e.target.checked))}
                color="primary"
              />
            }
            label={t`Correlate Sites Mode`}
          />
        )}
      </div>
      <div
        className={`flex flex-col absolute h-full max-h-full bg-white p-3 overflow-hidden cursor-default ${
          classes.outerWrapper
        } ${open ? '' : classes.closed}`}
        data-testid="map-filters-wrapper"
      >
        <Header
          title={t`Filters`}
          buttonsContainerClasses={'justify-end'}
          buttons={<>{togglerButton(classes.togglerOpened)}</>}
          dataTestId="map-filters-header"
        />
        <div className={`flex flex-col pr-2 overflow-y-auto overflow-x-hidden ${classes.innerWrapper}`}>
          {isNil(isCorrelationInfo) && (
            <CustomCheckbox
              label={t`Level/Spread Values`}
              newEditableLook
              checked={showLNS}
              onChange={(e) => setData({ showLNS: e.target.checked })}
              dataTestId="map-filters-showLNS-checkbox"
            />
          )}
          {isNil(isCorrelationInfo) && (
            <CustomCheckbox
              label={t`CSQ`}
              newEditableLook
              checked={showCSQ}
              onChange={(e) => setData({ showCSQ: e.target.checked })}
              dataTestId="map-filters-showCSQ-checkbox"
            />
          )}
          {isNil(isCorrelationInfo) && (
            <CustomCheckbox
              label={t`Site ID`}
              newEditableLook
              checked={showName}
              onChange={(e) => setData({ showName: e.target.checked })}
              dataTestId="map-filters-showName-checkbox"
            />
          )}
          {isNil(isCorrelationInfo) && (
            <CustomSelect
              newEditableLook
              placeholder={t`Show`}
              label={null}
              onChange={(data) => setData({ showOption: (data as CustomSelectItem).value })}
              options={showOptionsFn()}
              value={showOptionsFn().find((el) => el.value === showOption) ?? null}
              isDisabled={false}
              isError={false}
              className={classes.select}
              minWidth={124}
              dataTestId="map-filters-showOption-select"
            />
          )}
          {isNil(isCorrelationInfo) && (
            <CustomCheckbox
              label={t`Show sites without location`}
              newEditableLook
              checked={showSitesWithoutLocation}
              onChange={(e) => setData({ showSitesWithoutLocation: e.target.checked })}
              dataTestId="map-filters-showSitesWithoutLocation-checkbox"
            />
          )}
          {isNil(isCorrelationInfo) && (
            <CustomCheckbox
              label={t`GIS`}
              newEditableLook
              checked={showGIS}
              onChange={(e) => setData({ showGIS: e.target.checked })}
              dataTestId="map-filters-showGIS-checkbox"
            />
          )}
          <div className={`${classes.togglersContainer} ${classes.innerWrapper}`}>
            {isDma && (
              <>
                <CustomCheckbox
                  label={t`GIS Pipe Data`}
                  newEditableLook
                  checked={showGISPipeData}
                  disabled={!showGIS}
                  onChange={(e) => setData({ showGISPipeData: e.target.checked })}
                  dataTestId="map-filters-showGISPipeData-checkbox"
                />
                {isNil(isCorrelationInfo) && (
                  <CustomCheckbox
                    style={{ marginLeft: '15px' }}
                    label={t`Distance Measurement Tool`}
                    newEditableLook
                    checked={showMeasurementTool}
                    disabled={!showGIS || !showGISPipeData}
                    onChange={(e) => setData({ showMeasurementTool: e.target.checked })}
                    dataTestId="map-filters-showMeasurementTool-checkbox"
                  />
                )}
                {isNil(isCorrelationInfo) && (
                  <CustomCheckbox
                    label={t`GIS Pipe Information`}
                    newEditableLook
                    checked={showGISPipeInformation}
                    disabled={!showGIS}
                    onChange={(e) => setData({ showGISPipeInformation: e.target.checked })}
                    dataTestId="map-filters-showGISPipeInformation-checkbox"
                  />
                )}
                {isNil(isCorrelationInfo) && (
                  <FormControl
                    style={{ marginLeft: '15px' }}
                    disabled={!showGISPipeInformation}
                    data-testid="map-filters-gisPipeMetricSystem-radio"
                  >
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      value={gisPipeMetricSystem}
                      name="radio-buttons-group"
                      onChange={(e) =>
                        setData({
                          gisPipeMetricSystem: e.target.value as EMetricSystem,
                        })
                      }
                    >
                      <FormControlLabel
                        value={EMetricSystem.Metric}
                        control={<Radio color="primary" />}
                        label={t`Metric`}
                      />
                      <FormControlLabel
                        value={EMetricSystem.Imperial}
                        control={<Radio color="primary" />}
                        label={t`Imperial`}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
                {isNil(isCorrelationInfo) && (
                  <CustomCheckbox
                    label={t`GIS Assets Data`}
                    newEditableLook
                    checked={showGISAssetsData}
                    disabled={!showGIS}
                    onChange={(e) => setData({ showGISAssetsData: e.target.checked })}
                    dataTestId="map-filters-showGISAssetsData-checkbox"
                  />
                )}
                {isNil(isCorrelationInfo) && (
                  <CustomCheckbox
                    label={t`GIS Assets Ids`}
                    newEditableLook
                    checked={showGISAssetIds}
                    disabled={!showGIS}
                    onChange={(e) => setData({ showGISAssetIds: e.target.checked })}
                    dataTestId="map-filters-showGISAssetIds-checkbox"
                  />
                )}
              </>
            )}
            {isNil(isCorrelationInfo) && (
              <CustomCheckbox
                label={t`GIS DMA Boundary`}
                newEditableLook
                checked={showGISDMAData}
                disabled={!showGIS}
                onChange={(e) => setData({ showGISDMAData: e.target.checked })}
                dataTestId="map-filters-showGISDMAData-checkbox"
              />
            )}
          </div>
          {isDma && (
            <>
              {isNil(isCorrelationInfo) && (
                <>
                  <CustomCheckbox
                    label={t`Correlations`}
                    newEditableLook
                    checked={showCorrelations}
                    onChange={(e) => setData({ showCorrelations: e.target.checked })}
                    dataTestId="map-filters-showCorrelations-checkbox"
                  />
                  <div className={`${classes.togglersContainer} ${classes.innerWrapper}`}>
                    <div>
                      <DateRangePicker
                        newEditableLook
                        placeholder={t`Correlation Time`}
                        value={dateRange[0] && dateRange[1] ? [dateRange[0], dateRange[1]] : null}
                        onChange={(dates) => setData({ dateRange: dates })}
                        disabled={!showCorrelations}
                        last12Months
                        clearable={false}
                        customMinWidth={200}
                        data-testid="map-filters-dateRange-date"
                      />
                    </div>
                    <CustomCheckbox
                      label={t`High Confidence Only`}
                      newEditableLook
                      checked={showHighConfidenceOnly}
                      disabled={!showCorrelations}
                      onChange={(e) => setData({ showHighConfidenceOnly: e.target.checked })}
                      dataTestId="map-filters-showHighConfidenceOnly-checkbox"
                    />
                    <CustomCheckbox
                      label={t`GIS Correlations Only`}
                      newEditableLook
                      checked={showGISCorrelations}
                      disabled={!showCorrelations}
                      onChange={(e) => setData({ showGISCorrelations: e.target.checked })}
                      dataTestId="map-filters-showGISCorrelations-checkbox"
                    />
                  </div>
                </>
              )}

              {isNil(isCorrelationInfo) && (
                <FeatureFlagInfoWrapper
                  flag={AppFeatureFlags.ThresholdModelling}
                  modalTitle={<Trans>Threshold Modelling</Trans>}
                  info={{
                    title: <Trans>TITLE_THRESHOLD_MODELLING_PREMIUM</Trans>,
                    description: <Trans>DESCRIPTION_THRESHOLD_MODELLING_PREMIUM</Trans>,
                    footer: <Trans>FOOTER_PREMIUM</Trans>,
                  }}
                  videos={['/feature_flag.mp4']}
                >
                  {() => (
                    <>
                      <div className="space-y-2">
                        <CustomCheckbox
                          label={t`Threshold Modelling`}
                          newEditableLook
                          checked={showThresholdModelling ?? false}
                          onChange={(e) =>
                            canUseThresholdModelling && setData({ showThresholdModelling: e.target.checked })
                          }
                          disabled={!canUseThresholdModelling}
                          data-testid="map-filters-showThresholdModelling-checkbox"
                        />
                        <div className={`${classes.togglersContainer} ${classes.innerWrapper}`}>
                          {Boolean(showThresholdModelling) && (
                            <Typography variant="body2">
                              <Trans>Threshold</Trans>: {thresholdModelling}
                            </Typography>
                          )}
                          <Slider
                            value={thresholdModelling}
                            min={8}
                            max={20}
                            marks={[
                              {
                                value: 8,
                                label: '8',
                              },
                              {
                                value: 20,
                                label: '20',
                              },
                            ]}
                            onChange={(_, value) =>
                              canUseThresholdModelling && setData({ thresholdModelling: value as number })
                            }
                            disabled={!canUseThresholdModelling || !showThresholdModelling}
                            valueLabelDisplay="off"
                            aria-labelledby="thresholdModelling-slider"
                            data-testid="map-filters-thresholdModelling-slider"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </FeatureFlagInfoWrapper>
              )}
            </>
          )}

          {isNil(isCorrelationInfo) && (
            <CustomCheckbox
              label={t`Survey Measurements`}
              newEditableLook
              checked={showSurveyMeasurements}
              onChange={(e) => setData({ showSurveyMeasurements: e.target.checked })}
              dataTestId="map-filters-showSurveyMeasurements-checkbox"
            />
          )}

          {isDma && !isNil(showLinkedSites) && (
            <CustomCheckbox
              label={t`Linked Sites`}
              newEditableLook
              checked={showLinkedSites}
              onChange={(e) => setData({ showLinkedSites: e.target.checked })}
              dataTestId="map-filters-showLinkedSites-checkbox"
            />
          )}

          {!isNil(showPRVLogger) && (
            <CustomCheckbox
              label={t`PRV Controller`}
              newEditableLook
              checked={showPRVLogger}
              onChange={(e) => setData({ showPRVLogger: e.target.checked })}
              dataTestId="map-filters-showPRVLogger-checkbox"
            />
          )}
          {!isNil(showFlowLogger) && (
            <CustomCheckbox
              label={t`Flow Logger`}
              newEditableLook
              checked={showFlowLogger}
              onChange={(e) => setData({ showFlowLogger: e.target.checked })}
              dataTestId="map-filters-showFlowLogger-checkbox"
            />
          )}
          {!isNil(showPressureLogger) && (
            <CustomCheckbox
              label={t`Pressure Logger`}
              newEditableLook
              checked={showPressureLogger}
              onChange={(e) => setData({ showPressureLogger: e.target.checked })}
              dataTestId="map-filters-showPressureLogger-checkbox"
            />
          )}
          {!isNil(showFlowPressureLogger) && (
            <CustomCheckbox
              label={t`Flow and Pressure Logger`}
              newEditableLook
              checked={showFlowPressureLogger}
              onChange={(e) => setData({ showFlowPressureLogger: e.target.checked })}
              dataTestId="map-filters-showFlowPressureLogger-checkbox"
            />
          )}

          {hasFeatureFlag(AppFeatureFlags.MicrostreamSurveys) && isNil(isCorrelationInfo) && (
            <>
              <CustomCheckbox
                label={t`Surveys`}
                newEditableLook
                checked={showSurveys}
                onChange={(e) => setData({ showSurveys: e.target.checked })}
                dataTestId="map-filters-showSurveys-checkbox"
              />
              <div className={`${classes.togglersContainer} ${classes.innerWrapper}`}>
                <div>
                  <DateRangePicker
                    newEditableLook
                    placeholder={t`Correlation Time`}
                    value={
                      surveysDateRange[0] && surveysDateRange[1] ? [surveysDateRange[0], surveysDateRange[1]] : null
                    }
                    onChange={(dates) => setData({ surveysDateRange: dates })}
                    disabled={!showSurveys}
                    last12Months
                    clearable={false}
                    customMinWidth={200}
                    data-testid="map-filters-surveysDateRange-date"
                  />
                </div>
                <CustomSelect
                  newEditableLook
                  placeholder={t`Status`}
                  label={null}
                  onChange={(data) => setData({ surveysStatus: (data as CustomSelectItem).value })}
                  options={[
                    { label: t`All`, value: null },
                    ...Object.entries(SurveyStatusOptionsFn()).map(([value, label]) => ({ label, value })),
                  ]}
                  value={
                    surveysStatus
                      ? {
                          label: SurveyStatusOptionsFn()[surveysStatus],
                          value: surveysStatus,
                        }
                      : null
                  }
                  isDisabled={!showSurveys}
                  isError={false}
                  className={classes.select}
                  minWidth={124}
                  dataTestId="map-filters-surveysStatus-select"
                />
                <CustomSelect
                  newEditableLook
                  placeholder={t`Result`}
                  label={null}
                  onChange={(data) => setData({ surveysResult: (data as CustomSelectItem).value })}
                  options={[
                    { label: t`All`, value: null },
                    ...Object.entries(SurveyResultOptionsFn()).map(([value, label]) => ({ label, value })),
                  ]}
                  value={
                    surveysResult
                      ? {
                          label: SurveyResultOptionsFn()[surveysResult],
                          value: surveysResult,
                        }
                      : null
                  }
                  isDisabled={!showSurveys}
                  isError={false}
                  className={classes.select}
                  minWidth={124}
                  dataTestId="map-filters-surveysResult-select"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Togglers;
